import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';

const AuthSidePanel = ({ imageUrl, title, subtitle }) => {
  return (
    <Box 
      sx={{
        flex: 1,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 4, md: 6 },
        color: 'white',
        overflow: 'hidden',
        height: '100%', // Ensure full height
        minHeight: { xs: '300px', md: 'auto' },
        background: 'linear-gradient(135deg, rgba(0, 8, 20, 0.9) 0%, rgba(0, 29, 61, 0.8) 100%)',
      }}
    >
      {/* Background image with enhanced overlay */}
      {imageUrl && (
        <Box 
          component="img"
          src={imageUrl}
          alt="Authentication visual"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%', // Full height
            objectFit: 'cover',
            zIndex: 0,
            filter: 'brightness(0.4) sepia(0.3) hue-rotate(160deg)',
            opacity: 0.6,
          }}
        />
      )}
      
      {/* Enhanced gradient overlay */}
      <Box 
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(135deg, 
              rgba(0, 255, 136, 0.1) 0%, 
              rgba(0, 153, 255, 0.2) 50%, 
              rgba(255, 51, 102, 0.1) 100%
            )`,
          zIndex: 1,
        }} 
      />
      
      {/* Animated floating elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '80px',
          height: '80px',
          background: 'radial-gradient(circle, rgba(0, 255, 136, 0.2) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(10px)',
          animation: 'float 6s ease-in-out infinite',
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          right: '10%',
          width: '60px',
          height: '60px',
          background: 'radial-gradient(circle, rgba(0, 153, 255, 0.2) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(8px)',
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />

      {/* Content - Centered properly */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          maxWidth: '400px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
          style={{ width: '100%' }}
        >
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <Typography 
              variant="h2" 
              fontWeight="800" 
              gutterBottom
              sx={{
                background: 'linear-gradient(45deg, #00ff88, #0099ff)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 4px 20px rgba(0, 255, 136, 0.3)',
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3rem' },
              }}
            >
              {title}
            </Typography>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <Typography 
              variant="h6" 
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: 1.6,
                fontWeight: 400,
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                fontSize: { xs: '1rem', md: '1.25rem' },
              }}
            >
              {subtitle}
            </Typography>
          </motion.div>
        </motion.div>
      </Box>
    </Box>
  );
};

export default AuthSidePanel;